import axios from 'axios';
import dayjs from 'dayjs';
import { get, map, reduce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import DataDogLogs from 'utils/DataDogLogs';
import Settings from 'utils/Settings';

import {
  RECOMMENDATION_API_ENDPOINTS,
  API_PATH,
  ENDPOINT_METHOD,
  TIMEOUT_MS,
  CLIENT_SECRET,
  CLIENT_NAME,
  API_NAME,
  QUERY_GET_CART_RECOMMENDATIONS,
} from './constants';

const RecommendationAPI = class {
  static get url() {
    const baseUrl = get(RECOMMENDATION_API_ENDPOINTS, Settings.environment, '');

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}${API_PATH}`;
  }

  static get queryGetCartRecommendations() {
    return {
      query: QUERY_GET_CART_RECOMMENDATIONS,
      operationName: 'getCartRecommendations',
    };
  }

  static _getFlattenedLineItems = items => reduce(items, (result, item) => [...result, item], []);

  static getCartLineItems = cart => {
    const items = get(cart, ['entities', 'item']);
    const flattenedItems = RecommendationAPI._getFlattenedLineItems(items);

    return map(flattenedItems, item => ({
      productId: get(item, ['product', 'masterId']),
      handle: get(item, ['product', 'handle']),
      sku: get(item, ['variant', 'sku']),
    }));
  };

  static fetchCartRecommendations = async cart => {
    const cartLineItems = RecommendationAPI.getCartLineItems(cart);
    const UUID = uuidv4();

    const variables = {
      geo: `${Settings.shopID}_WEB`,
      cartLineItems,
    };

    const body = {
      ...RecommendationAPI.queryGetCartRecommendations,
      variables,
    };

    const headers = {
      'X-Api-Key': CLIENT_SECRET,
      'X-Correlation-Id': UUID,
      'apollographql-client-name': CLIENT_NAME,
    };

    if (!RecommendationAPI.url) {
      throw new Error('Failed to find recommendation API base url.');
    }

    const initialTime = dayjs();

    try {
      const res = await axios.post(RecommendationAPI.url, body, {
        headers,
        timeout: TIMEOUT_MS,
      });

      // GraphQL can return some errors with a 200 response code
      if (res.errors) {
        DataDogLogs.error('Cart Recommendations API returned some errors', {
          xapi: API_NAME,
          'dd.trace_id': UUID,
          errors: res.errors,
        });
      }

      const duration = dayjs() - initialTime;
      const logResponse = {
        duration,
        xapi: API_NAME,
        'dd.trace_id': UUID,
        http: {
          status_code: res.status,
          method: ENDPOINT_METHOD,
          url_details: {
            path: API_PATH,
          },
        },
      };

      DataDogLogs.info('Cart Recommendations retrieved', logResponse);
      return { cartRecommendations: get(res, ['data', 'data', 'getCartRecommendations'], []) };
    } catch (error) {
      const duration = dayjs() - initialTime;
      const errorResponse = {
        duration,
        message: error.response.statusText,
        xapi: API_NAME,
        'dd.trace_id': UUID,
        http: {
          status_code: error.response.status,
          method: ENDPOINT_METHOD,
          url_details: {
            path: API_PATH,
          },
        },
      };

      DataDogLogs.error(error.response.statusText, errorResponse);
      return error.response.status;
    }
  };
};

export default RecommendationAPI;
